// ============================================================================
//  Cliente de /config — TypeScript sin framework, compilado con esbuild.
//  Puerto de controlador/app/web/config.html. Los tipos vienen de
//  src/shared/types, igual que client/pages/index.ts.
// ============================================================================
import type {
  BindingRow,
  EnergyBaselineRow,
  FanConfig,
  GasSensorInput,
  GasSensorRow,
  OutOfRootTopic,
  PortalRow,
  ScannerRow,
  ScannersPayload,
  ScannerZone,
  SettingsPayload,
  SetupStatus,
  ShiftSummaryRow,
  StatusLightRow,
} from '../../src/shared/types';
import { delJson, esc, getJson, postJson, toast } from '../shared/api';

let current: SettingsPayload | null = null;
let nextFanId = 1;

function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}
function inputEl(id: string): HTMLInputElement | null {
  return $(id) as HTMLInputElement | null;
}
function val(id: string): string {
  return inputEl(id)?.value ?? '';
}

/** addEventListener no acepta un listener async (Promise<void> != void): esto
 *  envuelve el handler y evita una promesa flotante en cada click de guardado. */
function onClick(id: string, handler: () => void | Promise<void>): void {
  $(id)?.addEventListener('click', () => {
    void handler();
  });
}

// ==========================================================================
//  Pestañas
// ==========================================================================
const TABS = [
  'conexion',
  'gases',
  'ventiladores',
  'telemetria',
  'linea-base',
  'turnos',
  'semaforo',
  'escaneres',
];

function showTab(tab: string): void {
  const target = TABS.includes(tab) ? tab : 'conexion';
  for (const t of TABS) {
    $(`section-${t}`)?.classList.toggle('active', t === target);
    $(`tab-${t}`)?.classList.toggle('active', t === target);
  }
}

$('cfg-tabs')?.addEventListener('click', (e) => {
  const tabEl = (e.target as HTMLElement).closest<HTMLElement>('.tab[data-tab]');
  if (!tabEl) return;
  const name = tabEl.dataset.tab as string;
  history.replaceState(null, '', `#${name}`);
  showTab(name);
});
window.addEventListener('hashchange', () => showTab(location.hash.slice(1)));

// ==========================================================================
//  Carga — GET /api/settings hidrata todo el formulario; reintenta si falla.
// ==========================================================================
let retryTimer: ReturnType<typeof setTimeout> | null = null;

async function load(): Promise<void> {
  try {
    const s = await getJson<SettingsPayload>('/api/settings');
    current = s;
    $('loading')?.setAttribute('hidden', '');
    $('content')?.removeAttribute('hidden');
    $('banner')?.classList.remove('show');
    hydrate(s);
  } catch {
    $('banner')?.classList.add('show');
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = setTimeout(load, 4000);
  }
}

function hydrate(s: SettingsPayload): void {
  if (inputEl('mqtt-host')) (inputEl('mqtt-host') as HTMLInputElement).value = s.mqtt.host;
  if (inputEl('mqtt-port'))
    (inputEl('mqtt-port') as HTMLInputElement).value = String(s.mqtt.port || 1883);
  if (inputEl('mqtt-user')) (inputEl('mqtt-user') as HTMLInputElement).value = s.mqtt.user;
  const pass = inputEl('mqtt-pass');
  if (pass) {
    pass.value = '';
    pass.placeholder = s.mqtt.passSet ? '••••••• (guardada — vacío = no cambiar)' : '';
  }
  if (inputEl('mqtt-topic-root'))
    (inputEl('mqtt-topic-root') as HTMLInputElement).value = s.mqtt.topicRoot;
  renderOutOfRoot(s.topicsOutOfRoot ?? [], s.mqtt.topicRoot);

  fillDatalist('dl-areas', s.areas);

  renderGasSensors(s.gasSensors ?? [], s.portals);
  renderVfdRows(s.vfdBindings, s.fans);
  renderFans(s.fans);
  renderFanMap(s.fans);

  if (inputEl('control-confirm-up'))
    (inputEl('control-confirm-up') as HTMLInputElement).value = String(s.control.confirmUpSeconds);
  if (inputEl('control-purge'))
    (inputEl('control-purge') as HTMLInputElement).value = String(s.control.purgeSeconds);
  if (inputEl('control-dwell'))
    (inputEl('control-dwell') as HTMLInputElement).value = String(s.control.dwellSeconds);
  if (inputEl('control-off-delay'))
    (inputEl('control-off-delay') as HTMLInputElement).value = String(s.control.offDelaySeconds);

  if (inputEl('energy-cosphi'))
    (inputEl('energy-cosphi') as HTMLInputElement).value = String(s.energy.cosphi);
  if (inputEl('energy-tariff'))
    (inputEl('energy-tariff') as HTMLInputElement).value = String(s.energy.tariff);
  if (inputEl('energy-currency'))
    (inputEl('energy-currency') as HTMLInputElement).value = s.energy.currency;

  renderLedRows(s.statusLights, s.portals);
  renderBaselineFanOptions(s.fans);
  renderShiftSummaryFanOptions(s.fans);
}

/** Tópicos configurados fuera de la raíz: se guardaron, pero nunca van a
 *  recibir dato (ver specs/20260903-02-raiz-de-topicos-unica.md). El servidor
 *  ya manda la lista resuelta y etiquetada en español — acá solo se dibuja. */
function renderOutOfRoot(rows: OutOfRootTopic[], root: string): void {
  const box = document.getElementById('mqtt-out-of-root');
  if (!box) return;
  if (!rows.length) {
    box.innerHTML = '';
    return;
  }
  box.innerHTML = `
    <div class="out-of-root">
      <strong>${rows.length} tópico(s) fuera de la raíz <code>${esc(root)}</code></strong>
      <p>Los de lectura no van a recibir ningún dato; los de escritura publican en un namespace
        ajeno del broker. Corregilos abajo, o cambiá la raíz.</p>
      <ul>
        ${rows
          .map(
            (r) =>
              `<li><span class="chip ${r.direction === 'write' ? 'chip-warn' : 'chip-danger'}">${
                r.direction === 'write' ? 'escritura' : 'lectura'
              }</span> ${esc(r.label)} — <code>${esc(r.topic)}</code></li>`,
          )
          .join('')}
      </ul>
    </div>`;
}

function fillDatalist(id: string, values: string[]): void {
  const el = $(id) as HTMLDataListElement | null;
  if (!el) return;
  el.innerHTML = values.map((v) => `<option value="${esc(v)}"></option>`).join('');
}

// ==========================================================================
//  Gases
// ==========================================================================
const GASES: Array<[string, string]> = [
  ['co', 'CO'],
  ['co2', 'CO₂'],
  ['o2', 'O₂'],
  ['no2', 'NO₂'],
];

/** Un tópico del sensor: uno por gas. */
function gasTopicRow(topic = '', variable = 'co'): HTMLElement {
  const row = document.createElement('div');
  row.className = 'gas-topic-row';
  row.innerHTML = `
    <input class="topic" placeholder="tópico (p. ej. …/sensor-gas/co)" value="${esc(topic)}">
    <select class="variable">${GASES.map(
      ([k, label]) => `<option value="${k}" ${k === variable ? 'selected' : ''}>${label}</option>`,
    ).join('')}</select>
    <button type="button" class="btn-del" title="Quitar tópico">✕</button>
  `;
  row.querySelector('.btn-del')?.addEventListener('click', () => row.remove());
  return row;
}

/** Un sensor de gas: nombre, bocamina, si controla la ventilación y sus
 *  tópicos. `data-id` = el sensor guardado (sin id, uno nuevo). */
function gasSensorCard(portals: PortalRow[], sensor?: GasSensorRow): HTMLElement {
  const card = document.createElement('div');
  card.className = 'gas-sensor';
  if (sensor) card.dataset.id = String(sensor.id);
  card.innerHTML = `
    <div class="gas-sensor-head">
      <input class="gs-name" placeholder="nombre del sensor" value="${esc(sensor?.name ?? '')}">
      <select class="gs-portal">
        <option value="">Sin bocamina</option>
        ${portals
          .map(
            (p) =>
              `<option value="${p.id}" ${p.id === sensor?.portalId ? 'selected' : ''}>${esc(p.name)}</option>`,
          )
          .join('')}
      </select>
      <label class="checkbox" title="Desmarcado: se ve, se guarda y alerta, pero no mueve el ventilador"><input class="gs-controls" type="checkbox" ${sensor?.controls === false ? '' : 'checked'}> controla la ventilación</label>
      <button type="button" class="btn-del" title="Quitar sensor">✕</button>
    </div>
    <div class="gs-topics"></div>
    <button type="button" class="btn btn-ghost gs-add-topic">Agregar tópico</button>
  `;
  const topics = card.querySelector('.gs-topics') as HTMLElement;
  for (const t of sensor?.topics.length ? sensor.topics : [{ topic: '', variable: 'co' }]) {
    topics.appendChild(gasTopicRow(t.topic, t.variable));
  }
  card
    .querySelector('.gs-add-topic')
    ?.addEventListener('click', () => topics.appendChild(gasTopicRow()));
  card.querySelector('.gas-sensor-head .btn-del')?.addEventListener('click', () => card.remove());
  return card;
}

function renderGasSensors(sensors: GasSensorRow[], portals: PortalRow[]): void {
  const container = $('gas-sensors');
  if (!container) return;
  container.innerHTML = '';
  for (const s of sensors) container.appendChild(gasSensorCard(portals, s));
}

function collectGasSensors(): GasSensorInput[] {
  const container = $('gas-sensors');
  if (!container) return [];
  return Array.from(container.querySelectorAll<HTMLElement>('.gas-sensor')).map((card) => ({
    id: card.dataset.id ? Number(card.dataset.id) : undefined,
    name: (card.querySelector('.gs-name') as HTMLInputElement).value.trim(),
    portalId: Number((card.querySelector('.gs-portal') as HTMLSelectElement).value) || null,
    controls: (card.querySelector('.gs-controls') as HTMLInputElement).checked,
    topics: Array.from(card.querySelectorAll('.gas-topic-row'))
      .map((row) => ({
        topic: (row.querySelector('.topic') as HTMLInputElement).value.trim(),
        variable: (row.querySelector('.variable') as HTMLSelectElement).value,
      }))
      .filter((t) => t.topic),
  }));
}

$('gas-add')?.addEventListener('click', () =>
  $('gas-sensors')?.appendChild(gasSensorCard(current?.portals ?? [])),
);
onClick('gas-save', async () => {
  const sensors = collectGasSensors();
  if (await postJson('/api/settings/gas-sensors', { sensors })) void load();
});

// ==========================================================================
//  VFD
// ==========================================================================
const VFD_VARS: Array<[string, string]> = [
  ['frequency', 'Frecuencia (Hz)'],
  ['current', 'Corriente (A)'],
  ['voltage', 'Voltaje (V)'],
  ['power', 'Potencia (kW)'],
  ['status', 'Estado (RUN/STOP)'],
];

function vfdRow(fans: FanConfig[], topic = '', variable = 'frequency', group = ''): HTMLElement {
  const row = document.createElement('div');
  row.className = 'grid4 binding-row';
  row.innerHTML = `
    <input class="topic" placeholder="tópico" value="${esc(topic)}">
    <select class="variable">${VFD_VARS.map(
      ([k, label]) => `<option value="${k}" ${k === variable ? 'selected' : ''}>${label}</option>`,
    ).join('')}</select>
    <select class="group">${fans
      .map(
        (f) =>
          `<option value="${f.id}" ${String(f.id) === group ? 'selected' : ''}>${esc(f.name)}</option>`,
      )
      .join('')}</select>
    <button type="button" class="btn-del">✕</button>
  `;
  row.querySelector('.btn-del')?.addEventListener('click', () => row.remove());
  return row;
}

function renderVfdRows(rows: BindingRow[], fans: FanConfig[]): void {
  const container = $('vfd-rows');
  if (!container) return;
  container.innerHTML = '';
  for (const r of rows) container.appendChild(vfdRow(fans, r.topic, r.variable, r.group));
}

$('vfd-add')?.addEventListener('click', () => {
  if (current) $('vfd-rows')?.appendChild(vfdRow(current.fans));
});
onClick('vfd-save', async () => {
  const points = collectBindingRows('vfd-rows');
  if (await postJson('/api/settings/vfd-bindings', { points })) void load();
});

function collectBindingRows(
  containerId: string,
): Array<{ topic: string; variable: string; group: string }> {
  const container = $(containerId);
  if (!container) return [];
  return Array.from(container.querySelectorAll('.binding-row'))
    .map((row) => ({
      topic: (row.querySelector('.topic') as HTMLInputElement).value.trim(),
      variable: (row.querySelector('.variable') as HTMLSelectElement).value,
      group: (row.querySelector('.group') as HTMLInputElement | HTMLSelectElement).value.trim(),
    }))
    .filter((p) => p.topic);
}

// ==========================================================================
//  Ventiladores
// ==========================================================================
/** Una fila de FanConfig.additionalTopics — mismo molde que vfdRow(), pero el
 *  nombre del parámetro lo escribe el operador en vez de elegirlo de una lista
 *  (ese es justamente el punto: no hay lista fija). */
function extraRow(parameter = '', topic = ''): HTMLElement {
  const row = document.createElement('div');
  row.className = 'grid3 extra-row';
  row.innerHTML = `
    <input class="ex-parameter" placeholder="parámetro (p. ej. torque)" value="${esc(parameter)}">
    <input class="ex-topic" placeholder="tópico de lectura" value="${esc(topic)}">
    <button type="button" class="btn-del">✕</button>
  `;
  row.querySelector('.btn-del')?.addEventListener('click', () => row.remove());
  return row;
}

function fanBox(f: Partial<FanConfig> & { id: number }): HTMLElement {
  const box = document.createElement('div');
  box.className = 'fanbox';
  box.dataset.id = String(f.id);
  box.innerHTML = `
    <div class="grid2">
      <label>Nombre<input class="fx-name" value="${esc(f.name ?? `Ventilador ${f.id}`)}"></label>
      <label>Tópico de escritura<input class="fx-topic" value="${esc(f.commandTopic ?? '')}"></label>
    </div>
    <div class="grid2">
      <label>Tópico de encendido/apagado<input class="fx-power-topic" placeholder="vacío = nunca se apaga" value="${esc(f.statusTopic ?? '')}"></label>
      <label class="kv">Envía <b>1</b> al encender y <b>0</b> al apagar. Sin este tópico el ventilador funciona siempre.</label>
    </div>
    <div class="grid2">
      <label>Tópico de lectura de frecuencia<input class="fx-feedback-topic" placeholder="vacío = sin realimentación" value="${esc(f.feedbackTopic ?? '')}"></label>
      <label class="kv">Hz que el propio ventilador reporta — se guarda junto a la consigna en el histórico.</label>
    </div>
    <div class="grid2">
      <label>Tópico de lectura de estado<input class="fx-status-feedback-topic" placeholder="vacío = sin realimentación" value="${esc(f.statusFeedbackTopic ?? '')}"></label>
      <label class="kv">Encendido/apagado (0/1) que el propio ventilador reporta — se guarda junto a la orden en el histórico.</label>
    </div>
    <div class="grid3">
      <label>Tópico de lectura de voltaje<input class="fx-voltage-topic" placeholder="vacío = sin lectura" value="${esc(f.voltageTopic ?? '')}"></label>
      <label>Tópico de lectura de corriente<input class="fx-current-topic" placeholder="vacío = sin lectura" value="${esc(f.currentTopic ?? '')}"></label>
      <label>Tópico de lectura de potencia<input class="fx-power-read-topic" placeholder="vacío = sin lectura" value="${esc(f.powerTopic ?? '')}"></label>
    </div>
    <div class="grid2">
      <label class="kv">Voltaje (V), corriente (A) y potencia (kW) que el propio variador reporta — se guardan junto al Hz en el histórico.</label>
      <label>Bocamina<select class="fx-area"><option value="">Todas (ocupación) / peor caso (gas)</option></select></label>
    </div>
    <div class="grid2">
      <label>Potencia nominal (kW)<input class="fx-power" type="number" value="${f.ratedPowerKw ?? 0}"></label>
      <label>Frecuencia nominal (Hz)<input class="fx-freq" type="number" value="${f.ratedFrequencyHz ?? 60}"></label>
    </div>
    <p class="kv">Niveles: ${esc((f.levels ?? []).map((l) => `${l} ${f.unit ?? 'Hz'}`).join(' · '))} — la escala de frecuencias es común a todos los ventiladores y se edita en el front (Configuración → Escala de frecuencias).</p>
    <div class="grid2">
      <label class="kv">Parámetros adicionales — cualquier otra magnitud que publique el variador (torque, temperatura…). Nombre en minúsculas, sin espacios ni tildes.</label>
      <button type="button" class="fx-extra-add">Agregar parámetro</button>
    </div>
    <div class="fx-extras"></div>
    <button type="button" class="btn-del">Eliminar ventilador</button>
  `;
  const extras = box.querySelector('.fx-extras');
  for (const a of f.additionalTopics ?? []) extras?.appendChild(extraRow(a.parameter, a.topic));
  box.querySelector('.fx-extra-add')?.addEventListener('click', () => {
    extras?.appendChild(extraRow());
  });
  fillPortalOptions(box.querySelector('.fx-area'), current?.portals ?? [], f.portalId ?? null);
  box.querySelector('.btn-del')?.addEventListener('click', () => {
    box.remove();
  });
  return box;
}

function fillPortalOptions(
  select: Element | null,
  portals: PortalRow[],
  selected: number | null,
): void {
  if (!select || !(select instanceof HTMLSelectElement)) return;
  for (const p of portals) {
    const opt = document.createElement('option');
    opt.value = String(p.id);
    opt.textContent = p.name;
    opt.selected = p.id === selected;
    select.appendChild(opt);
  }
}

function renderFans(fans: FanConfig[]): void {
  const container = $('fans');
  if (!container) return;
  container.innerHTML = '';
  nextFanId = Math.max(0, ...fans.map((f) => f.id)) + 1;
  for (const f of fans) container.appendChild(fanBox(f));
}

$('fan-add')?.addEventListener('click', () => {
  $('fans')?.appendChild(
    fanBox({
      id: nextFanId++,
      ratedFrequencyHz: 60,
      levels: current?.fanScale.levels,
      unit: current?.fanScale.unit,
    }),
  );
});

onClick('fan-save', async () => {
  const fans = Array.from(document.querySelectorAll('#fans .fanbox')).map((box) => {
    const el = box as HTMLElement;
    return {
      id: Number(el.dataset.id),
      name: (el.querySelector('.fx-name') as HTMLInputElement).value.trim(),
      commandTopic: (el.querySelector('.fx-topic') as HTMLInputElement).value.trim(),
      statusTopic: (el.querySelector('.fx-power-topic') as HTMLInputElement).value.trim(),
      feedbackTopic: (el.querySelector('.fx-feedback-topic') as HTMLInputElement).value.trim(),
      statusFeedbackTopic: (
        el.querySelector('.fx-status-feedback-topic') as HTMLInputElement
      ).value.trim(),
      voltageTopic: (el.querySelector('.fx-voltage-topic') as HTMLInputElement).value.trim(),
      currentTopic: (el.querySelector('.fx-current-topic') as HTMLInputElement).value.trim(),
      powerTopic: (el.querySelector('.fx-power-read-topic') as HTMLInputElement).value.trim(),
      // siempre se manda (aunque sea []): el servidor conserva lo persistido
      // sólo cuando el campo viene AUSENTE, así que quitar la última fila acá
      // tiene que poder limpiarlos de verdad.
      additionalTopics: Array.from(el.querySelectorAll('.extra-row'))
        .map((row) => ({
          parameter: (row.querySelector('.ex-parameter') as HTMLInputElement).value.trim(),
          topic: (row.querySelector('.ex-topic') as HTMLInputElement).value.trim(),
        }))
        .filter((a) => a.parameter || a.topic),
      portalId: Number((el.querySelector('.fx-area') as HTMLSelectElement).value) || null,
      ratedPowerKw: Number((el.querySelector('.fx-power') as HTMLInputElement).value) || 0,
      ratedFrequencyHz: Number((el.querySelector('.fx-freq') as HTMLInputElement).value) || 60,
      // levels/names/unit/atención/peligro ya no van: la escala es global
    };
  });
  if (await postJson('/api/settings/fans', { fans })) void load();
});

function renderFanMap(fans: FanConfig[]): void {
  const el = $('fan-map');
  if (!el) return;
  el.innerHTML = fans
    .map((f) => {
      const area = f.area || 'todas las áreas (peor caso de gas y ocupación global)';
      const warn = f.commandTopic ? '' : ' <span class="warn-chip">sin tópico</span>';
      const power = f.statusTopic ? '' : ' <span class="warn-chip">sin apagado</span>';
      return `<div class="fanmap-row"><b>${esc(f.name)}</b> → bocamina: ${esc(area)}${warn}${power}</div>`;
    })
    .join('');
}

// ==========================================================================
//  Suavizado / energía / semáforo — guardado directo, sin recargar todo
// ==========================================================================
onClick('control-save', async () => {
  await postJson('/api/settings/control', {
    confirmUpSeconds: Number(val('control-confirm-up')) || 0,
    purgeSeconds: Number(val('control-purge')) || 0,
    dwellSeconds: Number(val('control-dwell')) || 0,
    offDelaySeconds: Number(val('control-off-delay')) || 0,
  });
});

onClick('energy-save', async () => {
  await postJson('/api/settings/energy', {
    cosphi: Number(val('energy-cosphi')) || 0.85,
    tariff: Number(val('energy-tariff')) || 0,
    currency: val('energy-currency') || 'S/',
  });
});

// ==========================================================================
//  LEDs de semáforo
// ==========================================================================
function ledRow(
  portals: PortalRow[],
  topic = '',
  portalId: number | null = null,
  feedbackTopic = '',
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'grid4 binding-row';
  row.innerHTML = `
    <input class="topic" placeholder="…/color/set" value="${esc(topic)}">
    <input class="feedback" placeholder="…/color (opcional)" value="${esc(feedbackTopic)}">
    <select class="portal">
      <option value="">Peor caso global</option>
      ${portals
        .map(
          (p) =>
            `<option value="${p.id}" ${p.id === portalId ? 'selected' : ''}>${esc(p.name)}</option>`,
        )
        .join('')}
    </select>
    <button type="button" class="btn-del">✕</button>
  `;
  row.querySelector('.btn-del')?.addEventListener('click', () => row.remove());
  return row;
}

function renderLedRows(rows: StatusLightRow[], portals: PortalRow[]): void {
  const container = $('led-rows');
  if (!container) return;
  container.innerHTML = '';
  for (const r of rows) {
    container.appendChild(ledRow(portals, r.topic, r.portalId, r.feedbackTopic));
  }
}

$('led-add')?.addEventListener('click', () =>
  $('led-rows')?.appendChild(ledRow(current?.portals ?? [])),
);
onClick('led-save', async () => {
  const container = $('led-rows');
  const lights = container
    ? Array.from(container.querySelectorAll('.binding-row'))
        .map((row) => ({
          topic: (row.querySelector('.topic') as HTMLInputElement).value.trim(),
          feedbackTopic: (row.querySelector('.feedback') as HTMLInputElement).value.trim(),
          portalId: Number((row.querySelector('.portal') as HTMLSelectElement).value) || null,
        }))
        .filter((l) => l.topic)
    : [];
  if (await postJson('/api/settings/status-lights', { lights })) void load();
});

// ==========================================================================
//  Línea base de consumo (energy_baselines) — CRUD directo, no bulk-replace
//  como el resto de las pestañas: cada medición es su propia fila de la BD
//  (GET/POST/DELETE /api/energy/baselines, ver energy.route.ts) y se corrige
//  agregando una nueva o borrando la vieja, no editando en línea.
// ==========================================================================
let baselines: EnergyBaselineRow[] = [];

function renderBaselineFanOptions(fans: FanConfig[]): void {
  const select = $('eb-fan') as HTMLSelectElement | null;
  if (!select) return;
  const previous = select.value;
  select.innerHTML = fans.map((f) => `<option value="${f.id}">${esc(f.name)}</option>`).join('');
  if (fans.some((f) => String(f.id) === previous)) select.value = previous;
}

/** `datetime-local` no trae zona: el navegador lo interpreta en la hora local
 *  del operador, igual que espera measuredAt (epoch). Vacío = server decide
 *  "ahora" (measuredAt omitido en el body). */
function baselineMeasuredAt(): number | undefined {
  const raw = val('eb-date');
  if (!raw) return undefined;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? Math.round(ms / 1000) : undefined;
}

function renderBaselineRows(): void {
  const rows = $('eb-rows');
  if (!rows) return;
  const latestByFan = new Map<number, number>();
  for (const b of baselines) {
    const prev = latestByFan.get(b.fanId);
    if (prev === undefined || b.measuredAt > prev) latestByFan.set(b.fanId, b.measuredAt);
  }
  const sorted = [...baselines].sort(
    (a, b) => a.fanName.localeCompare(b.fanName) || b.measuredAt - a.measuredAt,
  );
  rows.innerHTML = sorted
    .map((b) => {
      const vigente = latestByFan.get(b.fanId) === b.measuredAt;
      return `
    <tr>
      <td>${esc(b.fanName)}${vigente ? ' <span class="chip chip-ok">vigente</span>' : ''}</td>
      <td class="mono">${b.hz}</td>
      <td class="mono">${b.powerKw}</td>
      <td class="mono">${esc(b.date)}</td>
      <td>${esc(b.note)}</td>
      <td class="row-actions">
        <button type="button" class="btn btn-ghost btn-sm" data-del-baseline="${b.id}">Borrar</button>
      </td>
    </tr>`;
    })
    .join('');
  $('eb-empty')?.toggleAttribute('hidden', baselines.length > 0);

  rows.querySelectorAll<HTMLButtonElement>('[data-del-baseline]').forEach((btn) => {
    btn.addEventListener('click', () => void deleteBaseline(Number(btn.dataset.delBaseline)));
  });
}

async function loadBaselines(): Promise<void> {
  try {
    baselines = await getJson<EnergyBaselineRow[]>('/api/energy/baselines');
    renderBaselineRows();
  } catch {
    // silencioso: el banner principal ya cubre el error de conexión al servidor
  }
}

async function deleteBaseline(id: number): Promise<void> {
  if (await delJson(`/api/energy/baselines/${id}`)) void loadBaselines();
}

onClick('eb-add', async () => {
  const fanId = Number(val('eb-fan'));
  const hz = Number(val('eb-hz'));
  const powerKw = Number(val('eb-power'));
  if (!fanId || !hz || !powerKw) {
    toast('Ventilador, Hz y potencia son obligatorios', true);
    return;
  }
  const ok = await postJson('/api/energy/baselines', {
    fanId,
    hz,
    powerKw,
    measuredAt: baselineMeasuredAt(),
    note: val('eb-note').trim(),
  });
  if (ok) {
    const hzEl = inputEl('eb-hz');
    const powerEl = inputEl('eb-power');
    const dateEl = inputEl('eb-date');
    const noteEl = inputEl('eb-note');
    if (hzEl) hzEl.value = '';
    if (powerEl) powerEl.value = '';
    if (dateEl) dateEl.value = '';
    if (noteEl) noteEl.value = '';
    void loadBaselines();
  }
});

// ==========================================================================
//  Resumen por turno (shift_summaries) — solo lectura + el procesado manual
//  de un turno puntual (GET/POST /api/energy/shift-summaries*, ver
//  energy.route.ts y specs/20260817-04-…). Nada acá se "guarda": el cálculo
//  corre solo al cerrar cada turno, esta pestaña únicamente lo muestra.
// ==========================================================================
function renderShiftSummaryFanOptions(fans: FanConfig[]): void {
  const select = $('ss-fan') as HTMLSelectElement | null;
  if (!select) return;
  const previous = select.value;
  select.innerHTML =
    '<option value="">Todos</option>' +
    fans.map((f) => `<option value="${f.id}">${esc(f.name)}</option>`).join('');
  if (previous && fans.some((f) => String(f.id) === previous)) select.value = previous;
}

function fmtKwh(n: number | null): string {
  return n == null ? '—' : n.toFixed(2);
}

function fmtCoverage(ratio: number): string {
  const pct = Math.round(ratio * 100);
  const cls = ratio >= 0.95 ? 'chip-ok' : ratio >= 0.5 ? 'chip-warn' : 'chip-danger';
  return `<span class="chip ${cls}">${pct}%</span>`;
}

function renderShiftSummaryRows(rows: ShiftSummaryRow[]): void {
  const body = $('ss-rows');
  if (!body) return;
  body.innerHTML = rows
    .map((r) => {
      const savings =
        r.savingsKwh == null
          ? '—'
          : `${fmtKwh(r.savingsKwh)}${r.savingsKwh < 0 ? ' <span class="chip chip-warn">↑</span>' : ''}`;
      const cost = r.savingsCost == null ? '—' : `${r.currency} ${r.savingsCost.toFixed(2)}`;
      return `
    <tr>
      <td class="mono">${esc(r.date)}</td>
      <td>${r.shift === 'day' ? 'Día' : 'Noche'}</td>
      <td>${esc(r.fanName)}</td>
      <td>${fmtCoverage(r.coverageRatio)}</td>
      <td class="mono">${fmtKwh(r.realKwh)}</td>
      <td class="mono">${fmtKwh(r.baselineKwh)}</td>
      <td class="mono">${savings}</td>
      <td class="mono">${cost}</td>
      <td class="mono">${r.avgPowerKw == null ? '—' : r.avgPowerKw.toFixed(1)}</td>
    </tr>`;
    })
    .join('');
  $('ss-empty')?.toggleAttribute('hidden', rows.length > 0);
}

async function loadShiftSummaries(): Promise<void> {
  const fanId = val('ss-fan');
  const shift = val('ss-shift');
  const limit = Number(val('ss-limit')) || 30;
  const params = new URLSearchParams({ limit: String(limit), order: 'desc' });
  if (fanId) params.set('fanId', fanId);
  if (shift) params.set('shift', shift);
  // ss-from/ss-to son <input type="date"> ("YYYY-MM-DD") — el servidor los
  // acepta tal cual (parseRangeBound en energy.route.ts), sin convertir a
  // epoch acá.
  const from = val('ss-from');
  const to = val('ss-to');
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  try {
    const data = await getJson<{ rows: ShiftSummaryRow[] }>(
      `/api/energy/shift-summaries?${params.toString()}`,
    );
    renderShiftSummaryRows(data.rows);
  } catch {
    // silencioso: el banner principal ya cubre el error de conexión al servidor
  }
}

onClick('ss-refresh', loadShiftSummaries);
$('ss-fan')?.addEventListener('change', () => void loadShiftSummaries());
$('ss-shift')?.addEventListener('change', () => void loadShiftSummaries());
$('ss-from')?.addEventListener('change', () => void loadShiftSummaries());
$('ss-to')?.addEventListener('change', () => void loadShiftSummaries());

onClick('ss-process', async () => {
  const date = val('ss-process-date');
  const shift = val('ss-process-shift');
  if (!date) {
    toast('Elegí la fecha del turno', true);
    return;
  }
  const force = (inputEl('ss-process-force') as HTMLInputElement | null)?.checked ?? false;
  const ok = await postJson('/api/energy/shift-summaries/process', { date, shift, force });
  if (ok) void loadShiftSummaries();
});

// ==========================================================================
//  Conexión
// ==========================================================================
onClick('mqtt-save', async () => {
  const ok = await postJson('/api/settings/mqtt', {
    host: val('mqtt-host').trim(),
    port: Number(val('mqtt-port')) || 1883,
    user: val('mqtt-user').trim(),
    pass: val('mqtt-pass'),
    topicRoot: val('mqtt-topic-root').trim(),
  });
  if (ok) {
    const pass = inputEl('mqtt-pass');
    if (pass) pass.value = '';
    void load();
  }
});

onClick('mqtt-disconnect', async () => {
  if (await postJson('/api/settings/mqtt/disconnect', {})) void load();
});

onClick('banner-retry', () => load());

// ==========================================================================
//  Escáneres — bocaminas + asignación de bocamina/zona/orden por escáner.
//  Se cargan aparte de /api/settings porque cambian solas (descubrimiento
//  indistinto al tópico, ver topic-registry.ts): se refrescan con un timer
//  propio, igual que loadChecklist().
//
//  `scannersDirty` evita que ese refresco automático pise al operador
//  mientras edita: se prende con cualquier interacción dentro de las dos
//  tablas (escribir, tocar un select, agregar una fila) y se apaga recién
//  tras un guardado exitoso — ahí el refresco es intencional (fuerza la
//  vista al estado recién persistido) y se dispara con force=true.
// ==========================================================================
let nextPortalId = -1; // negativos: altas todavía sin id real de la BD
let scannersDirty = false;

function markScannersDirty(): void {
  scannersDirty = true;
}

function portalRow(p: Partial<PortalRow> = {}): HTMLElement {
  const id = p.id ?? nextPortalId--;
  const row = document.createElement('div');
  row.className = 'grid4 binding-row';
  row.dataset.id = String(id);
  row.innerHTML = `
    <input class="pt-name" placeholder="bm1" value="${esc(p.name ?? '')}">
    <input class="pt-base" placeholder="mina/bm1" value="${esc(p.topicBase ?? '')}">
    <label class="checkbox"><input class="pt-active" type="checkbox" ${p.isActive !== false ? 'checked' : ''}> activa</label>
    <button type="button" class="btn-del">✕</button>
  `;
  row.querySelector('.btn-del')?.addEventListener('click', () => {
    row.remove();
    markScannersDirty();
  });
  return row;
}

function renderPortalRows(portals: PortalRow[]): void {
  const container = $('portal-rows');
  if (!container) return;
  container.innerHTML = '';
  for (const p of portals) container.appendChild(portalRow(p));
}

$('portal-add')?.addEventListener('click', () => {
  $('portal-rows')?.appendChild(portalRow());
  markScannersDirty();
});
$('portal-rows')?.addEventListener('input', markScannersDirty);
$('portal-rows')?.addEventListener('change', markScannersDirty);

onClick('portal-save', async () => {
  const rows = Array.from(document.querySelectorAll('#portal-rows .binding-row'));
  const portals = rows.map((row) => {
    const el = row as HTMLElement;
    const id = Number(el.dataset.id);
    return {
      id: id > 0 ? id : undefined,
      name: (el.querySelector('.pt-name') as HTMLInputElement).value.trim(),
      topicBase: (el.querySelector('.pt-base') as HTMLInputElement).value.trim(),
      isActive: (el.querySelector('.pt-active') as HTMLInputElement).checked,
    };
  });
  if (await postJson('/api/settings/portals', { portals })) {
    scannersDirty = false;
    void loadScanners(true);
  }
});

// ==========================================================================
//  Cadena de lectores — el operador SOLO ORDENA
//
//  La pantalla vieja pedía dos campos por escáner: "Zona" (exterior /
//  intermedio / interior) y "Orden (1 = más interno)". Son dos formas de
//  escribir un único hecho físico —en qué punto del socavón está instalado el
//  lector— y mantenerlos coherentes quedaba a cargo del operador, con el
//  número además al revés de como uno cuenta al entrar. Nada avisaba si se
//  contradecían; el motor de presencia simplemente contaba los ingresos al
//  revés (presence.service.ts:196-252).
//
//  Ahora se pide la posición una sola vez: la cadena se dibuja de la
//  superficie hacia el fondo y el operador mueve las tarjetas hasta que
//  coincida con la instalación real. `zone` y `sortOrder` se DERIVAN de esa
//  posición al guardar (ver chainAssignments) y no se muestran como campos.
//  El formato persistido no cambió: sigue siendo 1 = el más interno, con los
//  terminales en las puntas — scanners.service.ts::validateChains verifica
//  exactamente esa derivación del lado del servidor.
// ==========================================================================

/** Un lugar en la cadena, ya traducido al vocabulario del sistema. `n === 1`
 *  no tiene terminal a propósito: una cadena de un solo lector no puede medir
 *  dirección, y firmar ingresos/salidas con ella sería inventarlas (misma
 *  regla que valida el servidor). */
function chainRole(index: number, n: number): { zone: ScannerZone; label: string; kind: string } {
  if (n === 1) return { zone: 'middle', label: 'sin terminal', kind: 'none' };
  if (index === 0) return { zone: 'exterior', label: 'confirma SALIDA', kind: 'out' };
  if (index === n - 1) return { zone: 'interior', label: 'confirma INGRESO', kind: 'in' };
  return { zone: 'middle', label: 'paso intermedio', kind: 'mid' };
}

interface ChainModel {
  portals: PortalRow[];
  byId: Map<string, ScannerRow>;
  /** portalId → ids en orden físico: [0] el más cercano a la superficie. */
  chains: Map<number, string[]>;
  pool: string[];
  enrollmentId: string | null;
}

let chain: ChainModel | null = null;

/** El estado persistido guarda el orden invertido (1 = más interno), así que
 *  la cadena visual es ese orden descendente. Los empates (datos viejos con
 *  orden 0 repetido) caen al final en orden estable. */
function buildChainModel(payload: ScannersPayload): ChainModel {
  const byId = new Map(payload.scanners.map((s) => [s.id, s]));
  const chains = new Map<number, string[]>();
  for (const p of payload.portals) chains.set(p.id, []);
  const pool: string[] = [];
  let enrollmentId: string | null = null;

  for (const s of payload.scanners) {
    if (s.isEnrollment) {
      // solo puede haber uno; si la BD trae más (dato viejo), el resto vuelve
      // al pool y el operador decide.
      if (enrollmentId === null) {
        enrollmentId = s.id;
        continue;
      }
      pool.push(s.id);
      continue;
    }
    const target = s.portalId !== null ? chains.get(s.portalId) : undefined;
    if (target) target.push(s.id);
    else pool.push(s.id);
  }

  for (const ids of chains.values()) {
    ids.sort((a, b) => (byId.get(b)?.sortOrder ?? 0) - (byId.get(a)?.sortOrder ?? 0));
  }
  return { portals: payload.portals, byId, chains, pool, enrollmentId };
}

function scannerName(s: ScannerRow): string {
  return s.label || s.segment || s.mac || s.id;
}

function scannerState(s: ScannerRow): string {
  if (s.online) return 'en línea';
  if (!Number.isFinite(s.lastSeenSecondsAgo)) return 'nunca visto';
  return `hace ${Math.round(s.lastSeenSecondsAgo)}s`;
}

/** Saca un escáner de donde esté (cadena, pool u oficina). */
function detach(m: ChainModel, id: string): void {
  for (const ids of m.chains.values()) {
    const i = ids.indexOf(id);
    if (i >= 0) ids.splice(i, 1);
  }
  const p = m.pool.indexOf(id);
  if (p >= 0) m.pool.splice(p, 1);
  if (m.enrollmentId === id) m.enrollmentId = null;
}

function chainCard(m: ChainModel, id: string, index: number, n: number): string {
  const s = m.byId.get(id);
  if (!s) return '';
  const role = chainRole(index, n);
  return `
    <div class="ch-card${s.online ? '' : ' ch-down'}" data-id="${esc(id)}">
      <div class="ch-pos">${index + 1}º</div>
      <div class="ch-name" title="${esc(s.mac || s.id)}">${esc(scannerName(s))}</div>
      <div class="ch-meta">${esc(s.topicBase)}/${esc(s.segment)}<br>${esc(s.ip || 'sin ip')}</div>
      <div class="ch-role ch-role-${role.kind}">${role.label}</div>
      <div class="ch-state ${s.online ? 'ok' : 'down'}">${esc(scannerState(s))}</div>
      <div class="ch-btns">
        <button type="button" class="ch-move" data-dir="-1" ${index === 0 ? 'disabled' : ''} title="mover hacia la superficie">◀</button>
        <button type="button" class="ch-move" data-dir="1" ${index === n - 1 ? 'disabled' : ''} title="mover hacia el fondo">▶</button>
        <button type="button" class="ch-out" title="sacar de la cadena">✕</button>
      </div>
    </div>`;
}

function chainStatus(n: number): string {
  if (n === 0) {
    return `<div class="ch-status warn">Sin lectores: esta bocamina no detecta a nadie.</div>`;
  }
  if (n === 1) {
    return `<div class="ch-status warn">Un solo lector: alcanza para verlo pasar, pero no para saber si entra o sale. Agrega los demás.</div>`;
  }
  return `<div class="ch-status ok">${n} lectores en serie: el 1º confirma la salida y el ${n}º el ingreso.</div>`;
}

function renderChain(): void {
  const m = chain;
  const host = $('chain-portals');
  const poolHost = $('chain-pool');
  if (!m || !host || !poolHost) return;

  host.innerHTML = m.portals
    .map((p) => {
      const ids = m.chains.get(p.id) ?? [];
      const cards = ids
        .map((id, i) => chainCard(m, id, i, ids.length))
        .join('<div class="ch-link">→</div>');
      return `
        <div class="ch-portal" data-portal="${p.id}">
          <div class="ch-portal-h"><b>${esc(p.name)}</b><span class="kv">${esc(p.topicBase)}</span></div>
          <div class="ch-row">
            <div class="ch-end">Superficie</div>
            ${cards || '<div class="ch-empty">Sin lectores. Agrégalos desde «Sin ubicar», más abajo.</div>'}
            <div class="ch-end">Fondo</div>
          </div>
          ${chainStatus(ids.length)}
        </div>`;
    })
    .join('');

  poolHost.innerHTML = m.pool.length
    ? m.pool
        .map((id) => {
          const s = m.byId.get(id);
          if (!s) return '';
          return `
            <div class="ch-card ch-pool-card${s.online ? '' : ' ch-down'}" data-id="${esc(id)}">
              <div class="ch-name" title="${esc(s.mac || s.id)}">${esc(scannerName(s))}</div>
              <div class="ch-meta">${esc(s.topicBase)}/${esc(s.segment)}<br>${esc(s.ip || 'sin ip')}</div>
              <div class="ch-state ${s.online ? 'ok' : 'down'}">${esc(scannerState(s))}</div>
              <label class="ch-assign-l">Instalar en<select class="ch-assign"><option value="">—</option>${m.portals
                .map((p) => `<option value="${p.id}">${esc(p.name)}</option>`)
                .join('')}</select></label>
            </div>`;
        })
        .join('')
    : '<p class="kv">No hay lectores sueltos.</p>';

  renderEnrollment(m);
}

/** El de oficina se elige en su propia tarjeta, fuera de las bocaminas: es la
 *  misma exclusividad de antes (o está en una cadena, o es el de oficina —
 *  elegirlo acá lo saca de donde esté), pero dicha en el lenguaje de para qué
 *  sirve. El servidor la sigue forzando (scanners.service.ts::assignScanners). */
function renderEnrollment(m: ChainModel): void {
  const select = $('enrollment-select') as HTMLSelectElement | null;
  const state = $('enrollment-state');
  if (!select) return;
  const options = [...m.byId.values()].map((s) => {
    const where =
      s.id === m.enrollmentId
        ? ''
        : m.pool.includes(s.id)
          ? ' · sin ubicar'
          : ` · hoy en ${esc(m.portals.find((p) => (m.chains.get(p.id) ?? []).includes(s.id))?.name ?? '')}`;
    return `<option value="${esc(s.id)}" ${s.id === m.enrollmentId ? 'selected' : ''}>${esc(scannerName(s))} — ${esc(s.ip || s.mac || s.id)}${where}</option>`;
  });
  select.innerHTML = `<option value="">Ninguno</option>${options.join('')}`;
  if (!state) return;
  const s = m.enrollmentId ? m.byId.get(m.enrollmentId) : null;
  if (!s) {
    state.className = 'enroll-state warn';
    state.textContent = 'Sin lector de oficina: no se pueden dar de alta tarjetas nuevas.';
    return;
  }
  state.className = `enroll-state ${s.online ? 'ok' : 'warn'}`;
  state.textContent = s.online ? 'en línea' : `sin señal — ${scannerState(s)}`;
}

$('chain-portals')?.addEventListener('click', (ev) => {
  const m = chain;
  const target = ev.target;
  if (!m || !(target instanceof HTMLElement)) return;
  const card = target.closest<HTMLElement>('.ch-card');
  const portalEl = target.closest<HTMLElement>('.ch-portal');
  const id = card?.dataset.id;
  if (!id || !portalEl) return;
  const ids = m.chains.get(Number(portalEl.dataset.portal));
  if (!ids) return;

  if (target.closest('.ch-move')) {
    const dir = Number(target.closest<HTMLElement>('.ch-move')?.dataset.dir ?? 0);
    const i = ids.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j] as string, ids[i] as string];
    markScannersDirty();
    renderChain();
    return;
  }
  if (target.closest('.ch-out')) {
    detach(m, id);
    m.pool.push(id);
    markScannersDirty();
    renderChain();
  }
});

// Del pool a una cadena: entra por el fondo (es lo más común al ampliar una
// instalación hacia adentro) y desde ahí se mueve con las flechas.
$('chain-pool')?.addEventListener('change', (ev) => {
  const m = chain;
  const target = ev.target;
  if (!m || !(target instanceof HTMLSelectElement) || !target.classList.contains('ch-assign')) {
    return;
  }
  const id = target.closest<HTMLElement>('.ch-card')?.dataset.id;
  const portalId = Number(target.value);
  if (!id || !portalId) return;
  detach(m, id);
  m.chains.get(portalId)?.push(id);
  markScannersDirty();
  renderChain();
});

$('enrollment-select')?.addEventListener('change', (ev) => {
  const m = chain;
  const target = ev.target;
  if (!m || !(target instanceof HTMLSelectElement)) return;
  const previous = m.enrollmentId;
  if (previous && previous !== target.value) m.pool.push(previous);
  m.enrollmentId = null;
  if (target.value) {
    detach(m, target.value); // sale de la cadena donde estuviera
    m.enrollmentId = target.value;
  }
  markScannersDirty();
  renderChain();
});

/** Acá es donde la posición se vuelve `zone` + `sortOrder`: el índice 0 (el
 *  más cercano a la superficie) se lleva el orden más alto, porque la
 *  convención persistida es 1 = el más interno. */
function chainAssignments(m: ChainModel): Array<{
  id: string;
  portalId: number | null;
  zone: ScannerZone | null;
  sortOrder: number;
  label: string;
  isEnrollment: boolean;
}> {
  const out = [];
  for (const [portalId, ids] of m.chains) {
    ids.forEach((id, i) => {
      out.push({
        id,
        portalId,
        zone: chainRole(i, ids.length).zone,
        sortOrder: ids.length - i,
        label: m.byId.get(id)?.label ?? '',
        isEnrollment: false,
      });
    });
  }
  for (const id of m.pool) {
    out.push({
      id,
      portalId: null,
      zone: null,
      sortOrder: 0,
      label: m.byId.get(id)?.label ?? '',
      isEnrollment: false,
    });
  }
  if (m.enrollmentId) {
    out.push({
      id: m.enrollmentId,
      portalId: null,
      zone: null,
      sortOrder: 0,
      label: m.byId.get(m.enrollmentId)?.label ?? '',
      isEnrollment: true,
    });
  }
  return out;
}

// Los dos botones guardan el MISMO estado completo (la pantalla es una sola
// foto de todos los escáneres): tener uno por tarjeta es solo para no obligar
// a bajar hasta el final después de cambiar el lector de oficina.
async function saveScanners(): Promise<void> {
  if (!chain) return;
  if (await postJson('/api/settings/scanners', { scanners: chainAssignments(chain) })) {
    scannersDirty = false;
    void loadScanners(true);
  }
}

onClick('scanner-save', saveScanners);
onClick('enrollment-save', saveScanners);

let scannersRetryTimer: ReturnType<typeof setTimeout> | null = null;

/** `force`: ignora `scannersDirty` — solo lo usan los guardados explícitos
 *  de arriba (el operador acaba de confirmar ese estado, no hay nada que
 *  proteger). El refresco periódico nunca pasa `force`. */
async function loadScanners(force = false): Promise<void> {
  try {
    const s = await getJson<ScannersPayload>('/api/scanners');
    if (scannersDirty && !force) return; // hay una edición sin guardar: no pisarla
    renderPortalRows(s.portals);
    chain = buildChainModel(s);
    renderChain();
  } catch {
    if (scannersRetryTimer) clearTimeout(scannersRetryTimer);
    scannersRetryTimer = setTimeout(() => void loadScanners(), 4000);
  }
}

// ==========================================================================
//  Checklist — un punto de estado por pestaña, desde /api/setup/status
// ==========================================================================
function setDot(id: string, ok: boolean, title: string): void {
  const el = $(id);
  if (!el) return;
  el.className = `dot ${ok ? 'ok' : 'pending'}`;
  el.title = title;
}

function renderChecklist(status: SetupStatus): void {
  setDot('tab-dot-conexion', status.mqtt.connected, status.mqtt.status);
  setDot(
    'tab-dot-gases',
    status.gas.ok,
    status.gas.configured ? 'configurado, sin datos aún' : 'sin tópicos configurados',
  );
  setDot(
    'tab-dot-ventiladores',
    status.fans.ok,
    status.fans.ok ? 'ok' : 'falta el tópico de algún ventilador',
  );
  setDot(
    'tab-dot-escaneres',
    status.scanners.ok,
    status.scanners.ok
      ? 'ok'
      : 'falta una bocamina con un escáner exterior y uno interior asignados',
  );
}

async function loadChecklist(): Promise<void> {
  try {
    renderChecklist(await getJson<SetupStatus>('/api/setup/status'));
  } catch {
    // silencioso: el banner principal ya cubre el error de conexión al servidor
  }
}

// ==========================================================================
//  Arranque
// ==========================================================================
showTab(location.hash.slice(1));
void load();
void loadChecklist();
void loadScanners();
void loadBaselines();
void loadShiftSummaries();
setInterval(() => void loadChecklist(), 6000);
setInterval(() => void loadScanners(), 5000);
